import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { Keyword } from "./protocol";
import { printedKeywords, renderKeywordBadges } from "./board/keyword-icons";
import { cardBackStyle, cardBackStyles, cardFrameStyles } from "./cosmetics";
import type { Frame } from "./board/field";
import { slugify } from "./art-url";
import { cardFaceStyles, fitCardFace, renderAbilityFace, renderCardFace, renderWalkerFace, type FaceArt } from "./card-face";

@customElement("card-art")
export class CardArt extends LitElement {
  @property() name = "";
  @property({ type: Boolean, reflect: true }) faceDown = false;
  @property({ type: Boolean, reflect: true }) tapped = false;
  @property({ type: Number }) oid: number | null = null;
  // A token's image key, which the server sends (engine/token_art). Its name's image is asked for
  // when that has none: a token that's a copy of a card has the card's.
  @property() artKey = "";
  // A card with no art to ask for (only the registry's cards and their tokens have art) shows as a
  // text card, with whichever of these it was given.
  @property({ type: Boolean }) artless = false;
  @property() manaCost = "";
  @property() typeLine = "";
  @property() oracleText = "";
  @property() pt = "";
  @property() ability = "";
  @property() loyalty = "";
  @property({ type: Boolean }) targetFocus = false;
  // An explicit list is authoritative in a game; static card browsers use standalone Oracle lines.
  @property({ attribute: false }) keywords: Keyword[] | null = null;
  // On the battlefield a card shows as a landscape crop of its art box with its name along the
  // bottom, framed in `frame`'s colour, which a drawn card and an ability card are drawn in too.
  @property({ type: Boolean }) crop = false;
  // A planeswalker stands upright on the battlefield instead: its name over its art, and its
  // loyalty in a shield along the bottom.
  @property({ type: Boolean }) upright = false;
  @property() frame: Frame = "colorless";
  // The card back its owner chose, shown face down, and the frame its controller chose, face up
  // (client/src/cosmetics.ts); "" is the table's own.
  @property() back = "";
  @property() look = "";

  // How many of the image keys have failed to load, in the order they're asked for.
  @state() private failedImages = 0;

  static styles = [cardBackStyles, cardFrameStyles, cardFaceStyles, css`
    :host { display: inline-block; }
    /* Width/height come from inherited CSS custom properties, which cross the
       shadow boundary, so app.ts sizes cards per context without a new property. */
    .card {
      width: var(--card-w, 110px); height: var(--card-h, 154px);
      border-radius: calc(var(--card-w, 110px) * 0.055); overflow: hidden;
      position: relative; transition: transform 160ms cubic-bezier(0.22,1,0.36,1);
      box-shadow: 0 1px 3px rgb(0 0 0 / 0.55);
      background: #1b1b1b;
    }
    .card.tapped { transform: rotate(8deg); }
    .card.upright { overflow: visible; background: none; box-shadow: none; }
    .card.target-focus { outline: 2px solid #67d9ff; outline-offset: 2px; box-shadow: 0 0 0 4px rgb(38 174 230 / 0.18), 0 0 22px rgb(38 174 230 / 0.55); }
    .card.face-down {
      box-sizing: border-box;
      background:
        radial-gradient(ellipse 60% 45% at 50% 50%, oklch(0.5 0.09 250 / 0.85) 0%, transparent 100%),
        radial-gradient(ellipse at 50% 36%, oklch(0.36 0.07 252) 0%, oklch(0.2 0.05 256) 72%);
      border: calc(var(--card-w, 110px) * 0.05) solid oklch(0.24 0.04 55);
      outline: 1px solid oklch(0.66 0.08 72 / 0.5); outline-offset: calc(var(--card-w, 110px) * -0.07);
    }
    img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .placeholder {
      width: 100%; height: 100%; display: flex; align-items: center;
      justify-content: center; color: oklch(0.92 0.01 85); text-align: center;
      padding: 8px; font-size: clamp(8px, calc(var(--card-w, 110px) * 0.1), 12px);
      box-sizing: border-box; line-height: 1.25;
      background: #2b2b2b;
    }
    .card.crop { box-sizing: border-box; border-radius: 6px; border: 2px solid var(--frame); box-shadow: inset 0 0 0 1px rgb(0 0 0 / 0.55), 0 2px 6px rgb(0 0 0 / 0.6); }
    .frame-W { --frame: #d8cfa6; }
    .frame-U { --frame: #3f86cf; }
    .frame-B { --frame: #6d4e8e; }
    .frame-R { --frame: #c9553a; }
    .frame-G { --frame: #3e8f57; }
    .frame-gold { --frame: #c8a248; }
    .frame-colorless { --frame: #8d949b; }
    /* The art box of a modern frame sits 8.5% in from the sides and 11-56% down the card. */
    .crop .crop-art { position: absolute; left: -10.2%; top: -25.9%; width: 120.5%; height: auto; max-width: none; object-fit: fill; }
    .crop .crop-blank { position: absolute; inset: 0; background: linear-gradient(160deg, #3a3a3a, #232323); }
    .crop .crop-name {
      position: absolute; left: 0; right: 0; bottom: 0; padding: 12px 6px 3px;
      font: 600 clamp(8px, calc(var(--card-w, 110px) * 0.085), 12px)/1.15 "Inter", system-ui, sans-serif;
      font-variant-caps: all-small-caps; letter-spacing: 0.05em; color: #f4f4f4; text-shadow: 0 1px 2px #000;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      background: linear-gradient(transparent, rgb(0 0 0 / 0.8) 60%);
    }
    .crop .crop-type { position: absolute; left: 6px; right: 6px; top: 5px; font: 500 clamp(7px, calc(var(--card-w, 110px) * 0.07), 11px)/1.2 "Inter", system-ui, sans-serif; color: #bbbbbb; }
    .oid-badge {
      position: absolute; bottom: 3px; left: 3px;
      background: oklch(0 0 0 / 0.62); color: oklch(0.92 0 0); font-size: 9px;
      padding: 1px 4px; border-radius: 3px; font-family: ui-monospace, monospace;
    }
    .full-card-keywords { position: absolute; top: 14%; right: 6%; left: 6%; display: flex; justify-content: flex-start; gap: 3px; flex-wrap: wrap; pointer-events: none; }
    .full-card-keywords .badge { display: inline-grid; place-items: center; box-sizing: border-box; width: clamp(9px, calc(var(--card-w, 110px) * 0.115), 28px); height: clamp(9px, calc(var(--card-w, 110px) * 0.115), 28px); padding: clamp(1px, calc(var(--card-w, 110px) * 0.015), 3px); border: 1px solid #ad955f; border-radius: 50%; color: #f3e4b8; background: #141411ed; box-shadow: 0 2px 5px #0009; pointer-events: none; }
    .full-card-keywords .keyword-icon, .full-card-keywords svg { display: block; width: 100%; height: 100%; fill: currentColor; }
    .full-card-keywords .keyword-text { width: auto; max-width: 100%; border-radius: 4px; padding-inline: 5px; font: 600 10px/1.2 var(--font-ui, sans-serif); }
  `];

  updated(changed: Map<string, unknown>): void {
    if (changed.has("name") || changed.has("artKey")) this.failedImages = 0;
    const face = this.renderRoot.querySelector<HTMLElement>(".face");
    if (!face) return;
    fitCardFace(face);
    if (document.fonts.status !== "loaded") void document.fonts.ready.then(() => fitCardFace(face));
  }

  render() {
    if (this.faceDown) {
      return html`<div class="card face-down with-back" style=${cardBackStyle(this.back)}></div>`;
    }
    const keys = this.artless ? [] : [...new Set([this.artKey, slugify(this.name)].filter((k) => k !== ""))];
    const key = keys[this.failedImages];
    const art: FaceArt = key === undefined ? null : { src: `/art/${key}.jpg`, failed: () => { this.failedImages += 1; } };
    if (this.ability !== "") {
      return html`<div class="card drawn">${renderAbilityFace({ source: this.name, frame: this.frame, art, kind: this.ability, text: this.oracleText, loyalty: this.loyalty })}</div>`;
    }
    if (this.upright) return html`<div class="card drawn upright">${renderWalkerFace({ name: this.name, frame: this.frame, art, loyalty: this.loyalty })}</div>`;
    if (this.crop) {
      return html`<div class="card crop frame-${this.frame} ${this.look ? `look-${this.look}` : ""}" role="img" aria-label=${this.name}>
        ${key !== undefined ? html`<img class="crop-art" src="/art/${key}.jpg" alt="" draggable="false" @error=${() => { this.failedImages += 1; }}>`
          : html`<div class="crop-blank"></div><span class="crop-type">${this.typeLine}</span>`}
        <span class="crop-name">${this.name}</span>
      </div>`;
    }
    const textCard = key === undefined && (this.typeLine !== "" || this.oracleText !== "");
    const cls = `card${textCard ? " drawn" : ""}${this.tapped ? " tapped" : ""}${this.targetFocus ? " target-focus" : ""}${this.look ? ` look-${this.look}` : ""}`;
    const keywords = textCard ? [] : this.keywords ?? printedKeywords(this.oracleText);
    return html`
      <div class="${cls}">
        ${key !== undefined
          ? html`<img src="/art/${key}.jpg" alt=${this.name} draggable="false" @error=${() => { this.failedImages += 1; }}>`
          : textCard
            ? renderCardFace({ name: this.name, frame: this.frame, art: null, manaCost: this.manaCost, typeLine: this.typeLine, text: this.oracleText, pt: this.pt })
            : html`<div class="placeholder">${this.name}</div>`}
        ${this.oid !== null ? html`<span class="oid-badge">#${this.oid}</span>` : ""}
        ${keywords.length ? html`<span class="full-card-keywords" aria-label="Card abilities">${renderKeywordBadges(keywords)}</span>` : ""}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "card-art": CardArt;
  }
}
